import { useCallback, useState } from 'react';
import { upNextQueue } from './help-gallery';
import type { HelpEntry } from './help-types';

export interface HelpPlayerState {
  current: HelpEntry | null;
  /** What plays after `current`, in order. */
  queue: HelpEntry[];
  /** Plays `entry`; the queue is the rest of `context` after it, or the Flows after a Flow. */
  play: (entry: HelpEntry, context: readonly HelpEntry[]) => void;
  /** Plays a video from the queue, keeping what follows it. */
  playQueued: (entry: HelpEntry) => void;
  /** Moves on to the first queued video. */
  advance: () => void;
  close: () => void;
}

/**
 * The one shared player (Q48). The queue is fixed when a video is picked, so playing on
 * from the Start here card keeps to that video's queue instead of the page it was opened from.
 */
export function useHelpPlayer(all: readonly HelpEntry[]): HelpPlayerState {
  const [state, setState] = useState<{ current: HelpEntry | null; queue: HelpEntry[] }>({ current: null, queue: [] });

  const play = useCallback(
    (entry: HelpEntry, context: readonly HelpEntry[]) => setState({ current: entry, queue: upNextQueue(entry, context, all) }),
    [all],
  );
  const playQueued = useCallback(
    (entry: HelpEntry) =>
      setState(({ queue }) => {
        const at = queue.findIndex((queued) => queued.id === entry.id);
        return { current: entry, queue: at === -1 ? [] : queue.slice(at + 1) };
      }),
    [],
  );
  const advance = useCallback(
    () => setState(({ current, queue }) => (queue.length ? { current: queue[0], queue: queue.slice(1) } : { current, queue })),
    [],
  );
  const close = useCallback(() => setState({ current: null, queue: [] }), []);

  return { ...state, play, playQueued, advance, close };
}
